interface LegalPageProps {
  section: 'terms' | 'privacy' | 'content-guidelines' | 'community-guidelines';
  onNavigate: (page: string, data?: any) => void;
}

export default function LegalPage({ section, onNavigate }: LegalPageProps) {
  return (
    <div className="min-h-screen bg-gray-950 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <button
          onClick={() => onNavigate('home')}
          className="flex items-center gap-2 text-ink-400 hover:text-ink-300 mb-8 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>

        <div className="flex gap-2 mb-8 flex-wrap">
          {[
            { key: 'terms', label: 'Terms of Service' },
            { key: 'privacy', label: 'Privacy Policy' },
            { key: 'content-guidelines', label: 'Content Guidelines' },
            { key: 'community-guidelines', label: 'Community Guidelines' },
          ].map(item => (
            <button
              key={item.key}
              onClick={() => onNavigate(item.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                section === item.key
                  ? 'bg-ink-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-gray-200 hover:bg-gray-700'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="prose prose-invert max-w-none">
          {section === 'terms' && <TermsOfService />}
          {section === 'privacy' && <PrivacyPolicy />}
          {section === 'content-guidelines' && <ContentGuidelines />}
          {section === 'community-guidelines' && <CommunityGuidelines />}
        </div>
      </div>
    </div>
  );
}

function TermsOfService() {
  return (
    <>
      <h1 className="text-3xl font-bold text-white mb-2">Terms of Service</h1>
      <p className="text-gray-400 mb-8">Last updated: February 25, 2026</p>

      <p className="text-gray-300 mb-6">
        Welcome to ScribeHaven. By accessing or using our platform, you agree to be bound by these Terms of Service. Please read them carefully before using our services.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">1. Acceptance of Terms</h2>
      <p className="text-gray-300 mb-4">
        By creating an account or using ScribeHaven, you agree to these Terms of Service, our Privacy Policy, Content Guidelines, and Community Guidelines. If you do not agree to any of these terms, you may not use the platform.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">2. Account Terms</h2>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>You must be at least 13 years old to create an account on ScribeHaven.</li>
        <li>You are responsible for maintaining the security of your account and password.</li>
        <li>You are responsible for all activity that occurs under your account.</li>
        <li>You may not use another user's account without permission.</li>
        <li>You must provide accurate and complete information when creating your account.</li>
        <li>One person may not maintain more than one account.</li>
      </ul>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">3. User Responsibilities</h2>
      <p className="text-gray-300 mb-4">
        As a user of ScribeHaven, you agree to:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Comply with all applicable laws and regulations.</li>
        <li>Not use the platform for any illegal or unauthorized purpose.</li>
        <li>Not transmit any malware, viruses, or harmful code.</li>
        <li>Not harass, abuse, or threaten other users.</li>
        <li>Not impersonate any person or entity.</li>
        <li>Not interfere with or disrupt the platform's functionality.</li>
      </ul>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">4. Content Ownership</h2>
      <p className="text-gray-300 mb-4">
        You retain all rights to the content you post on ScribeHaven. By publishing content on the platform, you grant ScribeHaven a non-exclusive, worldwide, royalty-free license to display, distribute, and promote your content within the platform. This license ends when you delete your content from ScribeHaven.
      </p>
      <p className="text-gray-300 mb-4">
        You represent and warrant that you own or have the necessary rights to all content you post, and that your content does not infringe on any third party's intellectual property rights.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">5. Platform Rights</h2>
      <p className="text-gray-300 mb-4">
        ScribeHaven reserves the right to:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Remove or modify content that violates our guidelines.</li>
        <li>Suspend or terminate accounts that violate these terms.</li>
        <li>Modify, update, or discontinue any part of the platform at any time.</li>
        <li>Set limits on certain features or restrict access to parts of the platform.</li>
        <li>Take any action we deem necessary to protect the safety and integrity of the platform.</li>
      </ul>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">6. Termination</h2>
      <p className="text-gray-300 mb-4">
        ScribeHaven may terminate or suspend your account at any time, with or without cause, and with or without notice. Upon termination, your right to use the platform ceases immediately. You may delete your account at any time. All provisions of these Terms which by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">7. Disclaimers</h2>
      <p className="text-gray-300 mb-4">
        ScribeHaven is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not guarantee that the platform will be uninterrupted, secure, or error-free. We are not responsible for the accuracy, reliability, or quality of any user-generated content.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">8. Limitation of Liability</h2>
      <p className="text-gray-300 mb-4">
        To the maximum extent permitted by law, ScribeHaven shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of the platform.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">9. Governing Law</h2>
      <p className="text-gray-300 mb-4">
        These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be resolved through binding arbitration in accordance with applicable arbitration rules.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">10. Changes to Terms</h2>
      <p className="text-gray-300 mb-4">
        We reserve the right to modify these Terms at any time. We will notify users of material changes by posting the updated Terms on the platform. Your continued use of ScribeHaven after changes are posted constitutes acceptance of the modified Terms.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">11. Contact</h2>
      <p className="text-gray-300 mb-4">
        If you have questions about these Terms of Service, please contact us through the platform's support channels.
      </p>
    </>
  );
}

function PrivacyPolicy() {
  return (
    <>
      <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
      <p className="text-gray-400 mb-8">Last updated: February 25, 2026</p>

      <p className="text-gray-300 mb-6">
        At ScribeHaven, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">1. Data We Collect</h2>
      <h3 className="text-lg font-medium text-gray-200 mt-6 mb-3">Information You Provide</h3>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Account information (username, email address, password)</li>
        <li>Profile information (bio, avatar)</li>
        <li>Content you create (stories, chapters, comments, reviews)</li>
        <li>Reading preferences and library selections</li>
        <li>Communications with other users and with us</li>
      </ul>

      <h3 className="text-lg font-medium text-gray-200 mt-6 mb-3">Information Collected Automatically</h3>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Device and browser information</li>
        <li>IP address and approximate location</li>
        <li>Usage data (pages visited, reading history, time spent)</li>
        <li>Interaction data (likes, follows, bookmarks)</li>
      </ul>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">2. Cookies</h2>
      <p className="text-gray-300 mb-4">
        We use cookies and similar technologies to maintain your session, remember your preferences, and analyze platform usage. Essential cookies are required for the platform to function. You can control non-essential cookies through your browser settings, though this may affect your experience.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">3. How We Use Your Data</h2>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>To provide and maintain the platform</li>
        <li>To personalize your experience and recommend content</li>
        <li>To communicate with you about your account and platform updates</li>
        <li>To enforce our terms of service and community guidelines</li>
        <li>To analyze usage patterns and improve the platform</li>
        <li>To detect and prevent fraud or abuse</li>
      </ul>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">4. Third-Party Sharing</h2>
      <p className="text-gray-300 mb-4">
        We do not sell your personal information. We may share data with:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Service providers who assist in operating the platform (hosting, analytics)</li>
        <li>Law enforcement when required by law or to protect rights and safety</li>
        <li>In connection with a merger, acquisition, or sale of assets (with notice)</li>
      </ul>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">5. Data Retention</h2>
      <p className="text-gray-300 mb-4">
        We retain your data for as long as your account is active or as needed to provide services. When you delete your account, we will delete your personal data within 30 days, except where we are required to retain it by law. Aggregated, anonymized data may be retained indefinitely for analytics purposes.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">6. Your Rights</h2>
      <p className="text-gray-300 mb-4">
        You have the right to:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Access and download your personal data</li>
        <li>Correct inaccurate information</li>
        <li>Delete your account and associated data</li>
        <li>Object to certain data processing activities</li>
        <li>Withdraw consent where processing is based on consent</li>
        <li>Lodge a complaint with a data protection authority</li>
      </ul>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">7. Data Security</h2>
      <p className="text-gray-300 mb-4">
        We implement industry-standard security measures to protect your data, including encryption of data in transit, secure password hashing, and regular security audits. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">8. Children's Privacy</h2>
      <p className="text-gray-300 mb-4">
        ScribeHaven is not intended for children under 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected data from a child under 13, we will take steps to delete it promptly.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">9. Changes to This Policy</h2>
      <p className="text-gray-300 mb-4">
        We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the updated policy on the platform. Your continued use of ScribeHaven after changes are posted constitutes acceptance of the updated policy.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">10. Contact</h2>
      <p className="text-gray-300 mb-4">
        If you have questions about this Privacy Policy or wish to exercise your data rights, please contact us through the platform's support channels.
      </p>
    </>
  );
}

function ContentGuidelines() {
  return (
    <>
      <h1 className="text-3xl font-bold text-white mb-2">Content Guidelines</h1>
      <p className="text-gray-400 mb-8">Last updated: February 25, 2026</p>

      <p className="text-gray-300 mb-6">
        ScribeHaven is a platform for creative expression. These Content Guidelines outline what is and isn't allowed to ensure a safe and enjoyable experience for all users.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">1. Allowed Content</h2>
      <p className="text-gray-300 mb-4">
        ScribeHaven welcomes a wide variety of creative writing, including:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Original fiction and non-fiction in all genres</li>
        <li>Serialized novels and short stories</li>
        <li>Poetry and creative essays</li>
        <li>Fan fiction (with proper attribution and disclaimers)</li>
        <li>Mature themes when properly tagged and rated</li>
      </ul>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">2. Prohibited Content</h2>
      <p className="text-gray-300 mb-4">
        The following content is strictly prohibited on ScribeHaven:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Content that sexualizes minors in any way</li>
        <li>Content that promotes or glorifies real-world violence, terrorism, or self-harm</li>
        <li>Hate speech targeting individuals or groups based on race, ethnicity, religion, gender, sexual orientation, disability, or other protected characteristics</li>
        <li>Content that contains real people in explicit sexual scenarios without consent</li>
        <li>Spam, advertising, or commercial solicitation disguised as stories</li>
        <li>Malware, phishing links, or other harmful technical content</li>
        <li>Content that violates any applicable law</li>
      </ul>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">3. Mature Content Rules</h2>
      <p className="text-gray-300 mb-4">
        Stories containing mature themes must:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Be clearly tagged as "Mature" in story settings</li>
        <li>Include appropriate content warnings at the beginning of relevant chapters</li>
        <li>Not appear in general/default search results without explicit user opt-in</li>
        <li>Follow all other content guidelines regardless of mature rating</li>
      </ul>
      <p className="text-gray-300 mb-4">
        Mature content includes: explicit sexual content, graphic violence, heavy drug use, and other themes intended for adult audiences.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">4. Copyright and Plagiarism</h2>
      <p className="text-gray-300 mb-4">
        ScribeHaven has zero tolerance for plagiarism. You must:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Only post content that you have written or have rights to publish</li>
        <li>Properly attribute any quotes, references, or inspirations</li>
        <li>Not copy or republish other authors' work without explicit written permission</li>
        <li>For fan fiction, include disclaimers about original source material ownership</li>
        <li>Not use AI-generated content without disclosure</li>
      </ul>
      <p className="text-gray-300 mb-4">
        If you believe your copyrighted work has been posted without permission, please submit a report through our platform. We will investigate and take appropriate action, including content removal and potential account suspension.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">5. Reporting Content</h2>
      <p className="text-gray-300 mb-4">
        If you encounter content that violates these guidelines:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Use the "Report" button available on stories, chapters, and comments</li>
        <li>Provide a clear description of the violation</li>
        <li>Do not engage with the violating content or user</li>
        <li>Our moderation team will review reports within 48 hours</li>
      </ul>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">6. Enforcement</h2>
      <p className="text-gray-300 mb-4">
        Violations of these Content Guidelines may result in:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Content removal</li>
        <li>Content demotion from rankings and recommendations</li>
        <li>Temporary account suspension</li>
        <li>Permanent account termination for severe or repeated violations</li>
        <li>Reporting to law enforcement where legally required</li>
      </ul>
    </>
  );
}

function CommunityGuidelines() {
  return (
    <>
      <h1 className="text-3xl font-bold text-white mb-2">Community Guidelines</h1>
      <p className="text-gray-400 mb-8">Last updated: February 25, 2026</p>

      <p className="text-gray-300 mb-6">
        ScribeHaven thrives because of its community of readers and writers. These guidelines help maintain a welcoming, respectful, and creative environment for everyone.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">1. Expected Behavior</h2>
      <p className="text-gray-300 mb-4">
        We expect all community members to:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Treat all users with respect and courtesy</li>
        <li>Provide constructive feedback on stories and chapters</li>
        <li>Celebrate diversity in stories, perspectives, and voices</li>
        <li>Support fellow writers with encouragement and honest critique</li>
        <li>Report violations rather than engaging in arguments</li>
        <li>Respect other users' boundaries and preferences</li>
      </ul>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">2. Harassment Policy</h2>
      <p className="text-gray-300 mb-4">
        ScribeHaven has zero tolerance for harassment. This includes but is not limited to:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Personal attacks, insults, or intimidation</li>
        <li>Persistent unwanted contact or messaging</li>
        <li>Doxxing (sharing personal information without consent)</li>
        <li>Brigading or coordinated attacks against users</li>
        <li>Threats of violence or harm</li>
        <li>Sexual harassment or unwanted sexual advances</li>
        <li>Stalking or following users across the platform to harass them</li>
        <li>Discrimination based on race, gender, sexuality, religion, disability, or any other characteristic</li>
      </ul>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">3. Spam Rules</h2>
      <p className="text-gray-300 mb-4">
        To maintain a quality experience, the following are considered spam:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Posting the same comment or review repeatedly</li>
        <li>Excessive self-promotion in comments on other users' stories</li>
        <li>Follow/unfollow manipulation to gain visibility</li>
        <li>Creating multiple accounts to artificially boost metrics</li>
        <li>Posting irrelevant links or advertisements</li>
        <li>Using bots or automated tools to interact with the platform</li>
        <li>Vote manipulation through fake accounts or coordinated efforts</li>
      </ul>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">4. Comments and Reviews</h2>
      <p className="text-gray-300 mb-4">
        When leaving comments and reviews:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li>Be constructive — focus on the writing, not the writer</li>
        <li>Avoid spoilers without proper warnings</li>
        <li>Do not leave reviews solely to promote your own work</li>
        <li>Respect the author's creative choices even if you disagree</li>
        <li>Use content warnings when discussing sensitive topics in comments</li>
      </ul>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">5. Consequences</h2>
      <p className="text-gray-300 mb-4">
        Violations of Community Guidelines will be addressed as follows:
      </p>
      <ul className="list-disc pl-6 text-gray-300 space-y-2 mb-4">
        <li><span className="text-yellow-400 font-medium">First offense:</span> Warning and content removal</li>
        <li><span className="text-orange-400 font-medium">Second offense:</span> Temporary suspension (7 days)</li>
        <li><span className="text-red-400 font-medium">Third offense:</span> Extended suspension (30 days)</li>
        <li><span className="text-red-500 font-medium">Severe violations:</span> Immediate permanent ban</li>
      </ul>
      <p className="text-gray-300 mb-4">
        Severe violations include threats of violence, content sexualizing minors, doxxing, and other actions that pose immediate harm. These may result in an immediate permanent ban without prior warnings.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">6. Appeals</h2>
      <p className="text-gray-300 mb-4">
        If you believe a moderation action was taken in error, you may appeal through the platform's support channels. Appeals will be reviewed by a different member of the moderation team within 7 business days.
      </p>

      <h2 className="text-xl font-semibold text-white mt-8 mb-4">7. Building a Better Community</h2>
      <p className="text-gray-300 mb-4">
        We believe that great stories deserve a great community. By participating in ScribeHaven, you are helping to build a space where creativity can flourish. Thank you for being part of our community and for treating fellow writers and readers with the respect they deserve.
      </p>
    </>
  );
}
